<script setup lang="ts">
/**
 * Weekly booking view for a single type of record: to the given specialist, or for the give room.
 * TODO: Term resource probably can be changed by term record.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/vue-query'
import { useAllBookingTypes } from '@/composables/useAllBookingTypes'
import {
  cancelBooking,
  createBooking,
  fetchBookingTypeSchedule,
  updateBooking,
  type ScheduleBooking,
  type UpdateBookingPayload,
} from '@/api/bookings'
import { fetchClient, fetchClients } from '@/api/clients'
import { fetchGroup } from '@/api/groups'
import { fetchPerformers } from '@/api/performers'
import { useAllPerformers } from '@/composables/useAllPerformers'
import { bookingSchedulePollingInterval } from '@/config/api'
import { useUserStore } from '@/stores/user_store'
import { useIsMobile } from '@/composables/useIsMobile'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'
import { intlLocale } from '@/i18n'
import { MS_PER_DAY } from '@/utils/time'
import { APP_TIME_ZONE, appDate, appDateString, appParts, appTimeOfDay } from '@/utils/appTime'
import DeleteConfirmSheet from '@/components/main/DeleteConfirmSheet.vue'

const { t } = useI18n()
const userStore = useUserStore()
const { isMobile } = useIsMobile()
const { hasRole } = usePermissions()
const myPersonId = computed<string | null>(() => userStore.userInfo?.person_id ?? null)
/**
 * Administration and Owners group members can book on behalf of anyone, not
 * just themselves — see the person picker in the create-booking dialog below —
 * and can manage (open/edit/cancel) any booking, not just their own (see
 * `canManage`). The `admin` / `owner` roles themselves are never granted by
 * those groups (the backend excludes them from group permissions), so a group
 * admin is recognised by `clients_edit` — held by Administration and Owners
 * but not by Performers or Finance, who also have `bookings_*`. Granular CRUD
 * is still gated separately via `canCreateBookings` / `canEditBookings` /
 * `canCancelBookings` below; this flag only controls the *scope* (self vs.
 * anyone) once an action is otherwise permitted.
 */
const canBookForOthers = computed(
  () =>
    hasRole(ResourceAccessRole.Admin) ||
    hasRole(ResourceAccessRole.Owner) ||
    hasRole(ResourceAccessRole.ClientsEdit),
)
/**
 * Granular CRUD roles — distinct from `canBookForOthers`'s "self vs.
 * anyone" scope above. Without these, a user with only `BookingsView`
 * (enough to see this tab at all, per App.vue's nav gate) could otherwise
 * still drag-select a free cell and create a booking, or — if also
 * Admin/Owner — edit/cancel anyone's via `canManage`, despite lacking
 * `BookingsCreate`/`BookingsEdit`/`BookingsDelete`.
 */
const canCreateBookings = computed(() => hasRole(ResourceAccessRole.BookingsCreate))
const canEditBookings = computed(() => hasRole(ResourceAccessRole.BookingsEdit))
const canCancelBookings = computed(() => hasRole(ResourceAccessRole.BookingsDelete))
/**
 * Only "own" bookings ever render detail (see `isOwn` below) — the person
 * shown is always the logged-in user, never looked up by `person_id`, since
 * other people's bookings are intentionally blind.
 */
const myDisplayName = computed<string>(() => {
  const info = userStore.userInfo
  if (!info) return t('tabs.booking.ownBooking')
  return `${info.first_name} ${info.last_name}`.trim()
})

// TODO: dynamically set in future
// TODO: change slots view. Idealy drag/drop rectangle is better.
const START_H = 8
const END_H = 24
const SLOTS = (END_H - START_H) * 2
// 30-minute slots from 08:00 up to (not including) 24:00.

// All grid math is in the app's fixed UTC+3 zone (see `@/utils/appTime`), not
// the browser's: "Monday 00:00" and every slot boundary are UTC+3 instants.
function mondayOf(date: Date): Date {
  const p = appParts(date)
  const diff = p.weekday === 0 ? -6 : 1 - p.weekday // weekday: 0 = Sunday .. 6 = Saturday
  return appDate(p.year, p.month, p.day + diff)
}

// Fixed offset (no DST), so a day is always MS_PER_DAY and a slot always 30 minutes.
function dateAtSlot(monday: Date, day: number, slot: number): Date {
  return new Date(monday.getTime() + day * MS_PER_DAY + (START_H * 60 + slot * 30) * 60_000)
}

function slotLabel(slot: number): string {
  const minutes = START_H * 60 + slot * 30
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
}

// ── Booking types (room/performer sub-tabs) ─────────────────────────────────
// Shares the ['bookingTypes'] query key with BookingTab's types table, so
// switching panes doesn't refetch.

const { bookingTypes, isDraining: typesIsDraining } = useAllBookingTypes()

const selectedTypeId = ref<string | null>(null)
watch(
  bookingTypes,
  (types) => {
    const firstId: string | null = types.length > 0 ? types[0]!.id : null
    if (selectedTypeId.value === null) selectedTypeId.value = firstId
    else if (!types.some((bt) => bt.id === selectedTypeId.value)) selectedTypeId.value = firstId
  },
  { immediate: true },
)

// ── Week navigation ──────────────────────────────────────────────────────────

const monday = ref<Date>(mondayOf(new Date()))
const weekDays = computed<Date[]>(() =>
  Array.from({ length: 7 }, (_, i) => new Date(monday.value.getTime() + i * MS_PER_DAY)),
)
const weekStartISO = computed(() => monday.value.toISOString())
const weekEndISO = computed(() => new Date(monday.value.getTime() + 7 * MS_PER_DAY).toISOString())

const weekLabel = computed(() => {
  // weekDays always has exactly 7 entries (see its definition above).
  const sunday = weekDays.value[6]!
  const fmt = new Intl.DateTimeFormat(intlLocale.value, {
    day: 'numeric',
    month: 'short',
    timeZone: APP_TIME_ZONE,
  })
  return `${fmt.format(monday.value)} — ${fmt.format(sunday)} ${appParts(sunday).year}`
})

const dayFmt = computed(
  () => new Intl.DateTimeFormat(intlLocale.value, { weekday: 'short', timeZone: APP_TIME_ZONE }),
)
const today = new Date()

function shiftWeek(deltaWeeks: number): void {
  monday.value = new Date(monday.value.getTime() + deltaWeeks * 7 * MS_PER_DAY)
}
function goToday(): void {
  monday.value = mondayOf(new Date())
}

// ── Schedule data ────────────────────────────────────────────────────────────

const scheduleQuery = useQuery({
  queryKey: ['bookingSchedule', selectedTypeId, weekStartISO],
  queryFn: () =>
    fetchBookingTypeSchedule(selectedTypeId.value as string, weekStartISO.value, weekEndISO.value),
  enabled: computed(() => selectedTypeId.value !== null),
  refetchInterval: bookingSchedulePollingInterval,
  refetchIntervalInBackground: false,
})

/**
 * `scheduleQuery.isPending` stays permanently `true` while the query is
 * `enabled: false` (no booking type selected yet, e.g. no booking types
 * exist) — a disabled query never runs, so it never leaves "pending". There
 * is nothing to load in that case, so the calendar renders its empty state
 * immediately instead of showing a stuck "Загрузка…" — this guard is what
 * makes that possible.
 */
const isScheduleLoading = computed(
  () => selectedTypeId.value !== null && scheduleQuery.isPending.value,
)

const bookings = computed<ScheduleBooking[]>(
  () => scheduleQuery.data.value?.bookings.filter((b) => b.status !== 'CANCELLED') ?? [],
)

const HALF_HOUR_MS = 30 * 60 * 1000

/**
 * Maps `"<day>-<slot>"` (both 0-indexed) to the booking occupying that
 * half-hour. A `Map` keyed by a composite string sidesteps
 * `noUncheckedIndexedAccess` array-index noise that a nested-array grid
 * would otherwise produce at every read site below.
 *
 * Walks the (at most 7) days instead of all 7×`SLOTS` cells per booking,
 * using `dateAtSlot` for each day's own start/end boundary — consistent
 * wall-clock math throughout, rather than mixing it with a raw
 * `cellStart + 30*60*1000` offset, which could disagree with `dateAtSlot`
 * by an hour on a DST transition day. Only the slot offset *within* an
 * already wall-clock-pinned day uses plain division, since both ends of
 * that division sit on the same calendar day.
 */
// Each day's {start, end} boundary depends only on `monday`, not on any
// individual booking — hoisted out of `cellBookings` below so N bookings
// does 7 `dateAtSlot` pairs total instead of 7×N.
const weekDayBoundaries = computed<{ startMs: number; endMs: number }[]>(() =>
  Array.from({ length: 7 }, (_, day) => ({
    startMs: dateAtSlot(monday.value, day, 0).getTime(),
    endMs: dateAtSlot(monday.value, day, SLOTS).getTime(),
  })),
)

const cellBookings = computed<Map<string, ScheduleBooking>>(() => {
  const map = new Map<string, ScheduleBooking>()
  for (const b of bookings.value) {
    const startMs = new Date(b.start_time).getTime()
    const endMs = new Date(b.end_time).getTime()
    for (let day = 0; day < 7; day++) {
      const { startMs: dayStartMs, endMs: dayEndMs } = weekDayBoundaries.value[day]!
      if (endMs <= dayStartMs || startMs >= dayEndMs) continue
      const overlapStart = Math.max(startMs, dayStartMs)
      const overlapEnd = Math.min(endMs, dayEndMs)
      // floor/ceil, not round: a booking not aligned to the grid (legacy
      // data, or created by any non-UI path) must still mark every slot it
      // touches even partially as occupied — rounding both ends to the same
      // slot would make a short, misaligned booking vanish from the grid
      // entirely.
      const firstSlot = Math.floor((overlapStart - dayStartMs) / HALF_HOUR_MS)
      const lastSlotExclusive = Math.ceil((overlapEnd - dayStartMs) / HALF_HOUR_MS)
      for (let slot = firstSlot; slot < lastSlotExclusive; slot++) {
        map.set(`${day}-${slot}`, b)
      }
    }
  }
  return map
})

function bookingAt(day: number, slot: number): ScheduleBooking | null {
  return cellBookings.value.get(`${day}-${slot}`) ?? null
}

function isSlotStart(day: number, slot: number): boolean {
  const b = bookingAt(day, slot)
  if (!b) return false
  return dateAtSlot(monday.value, day, slot).getTime() === new Date(b.start_time).getTime()
}
function isOwn(b: ScheduleBooking): boolean {
  return myPersonId.value !== null && b.person_id === myPersonId.value
}

/**
 * Admin/owner can manage (open, edit, cancel) any booking, not just their
 * own — matches their ability to *create* one for anyone via the person
 * picker. A regular staff member can still only manage their own.
 */
function canManage(b: ScheduleBooking): boolean {
  return isOwn(b) || canBookForOthers.value
}

/**
 * Names for bookings admin/owner can manage but don't own, resolved lazily
 * and safely: `fetchClient(id)` is the *singular* `GET /persons/{id}` — a
 * plain 200/404 lookup, unlike the bulk `/persons/clients/by-ids` endpoint
 * (used nowhere in this file), whose all-ids-unmatched case is a 403 that
 * the shared axios interceptor turns into an app-wide navigation.
 *
 * `null` is a cached value distinct from "not yet looked up" (absent from
 * the Map): a 404 (or any other failure) stores `null` so a permanently
 * unresolvable id — e.g. a deleted person — is never retried on every
 * subsequent `bookingSchedulePollingInterval` refetch; `personDisplayName`
 * treats "not found" and "not yet looked up" the same way (a placeholder).
 */
const personNameCache = ref<Map<string, string | null>>(new Map())

function personDisplayName(personId: string): string {
  if (personId === myPersonId.value) return myDisplayName.value
  return personNameCache.value.get(personId) ?? t('tabs.booking.unknownPerson')
}

watch(
  bookings,
  (list) => {
    if (!canBookForOthers.value) return
    const toResolve = new Set<string>()
    for (const b of list) {
      if (b.person_id !== myPersonId.value && !personNameCache.value.has(b.person_id)) {
        toResolve.add(b.person_id)
      }
    }
    if (toResolve.size === 0) return
    void Promise.allSettled(
      Array.from(toResolve).map(async (id) => {
        try {
          const person = await fetchClient(id)
          personNameCache.value.set(id, person.name)
        } catch {
          // Cached as unresolvable (see docstring above) rather than left
          // absent — a 404 (performer, or truly unknown id) is expected
          // here, not an error worth surfacing, but it also shouldn't be
          // retried on every poll.
          personNameCache.value.set(id, null)
        }
      }),
    ).then(() => {
      // Replace the Map so this watcher's own writes above (which mutated
      // it in place) are picked up by anything reading `personNameCache`.
      personNameCache.value = new Map(personNameCache.value)
    })
  },
  { immediate: true },
)

// ── Group sessions ──────────────────────────────────────────────────────────
const canViewGroups = computed(() => hasRole(ResourceAccessRole.GroupsView))
const canViewPerformers = computed(() => hasRole(ResourceAccessRole.StuffView))
const { performers: allPerformers } = useAllPerformers({ enabled: canViewPerformers })

const groupIds = computed(() => {
  const ids = new Set<string>()
  for (const b of bookings.value) if (b.group_id) ids.add(b.group_id)
  return Array.from(ids)
})
const groupQueries = useQueries({
  queries: computed(() =>
    groupIds.value.map((id) => ({
      queryKey: ['group', id],
      queryFn: () => fetchGroup(id),
      enabled: canViewGroups.value,
      staleTime: bookingSchedulePollingInterval,
    })),
  ),
})

const groupInfoById = computed(() => {
  const byPerformerId = new Map(allPerformers.value.map((p) => [p.id, p.name]))
  const map = new Map<string, { name: string; performers: string }>()
  for (const q of groupQueries.value) {
    const group = q.data
    if (!group) continue
    const mine = myPersonId.value !== null && group.performer_ids.includes(myPersonId.value)
    if (!canBookForOthers.value && !mine) continue
    map.set(group.id, {
      name: group.name ?? group.description ?? t('cards.group.defaultName'),
      performers: group.performer_ids
        .map((id) => byPerformerId.get(id))
        .filter((n): n is string => !!n)
        .join(', '),
    })
  }
  return map
})

function groupInfo(b: ScheduleBooking): { name: string; performers: string } | null {
  return b.group_id ? (groupInfoById.value.get(b.group_id) ?? null) : null
}

// 50 tints: hues spread by the golden angle across the wheel minus the
// greens (own bookings use green), with saturation and lightness cycling so
// neighbouring hues still read as different colours.
const TINT_COUNT = 50
const TINT_COLORS = Array.from({ length: TINT_COUNT }, (_, i) => ({
  hue: Math.round(160 + (((i * 137.508) % 360) * 300) / 360) % 360,
  sat: i % 2 === 0 ? 50 : 65,
  lit: [86, 78, 91][i % 3]!,
}))

/** Booking → colour key: one per group, or one per person for non-group bookings; null when the booking isn't tinted. */
function tintKey(b: ScheduleBooking): string | null {
  if (b.group_id) return groupInfoById.value.has(b.group_id) ? `g:${b.group_id}` : null
  if (isOwn(b)) return null
  // Own bookings keep the plain green style (`bsv__cell--own`); green isn't in the palette.
  return canBookForOthers.value ? `p:${b.person_id}` : null
}

/**
 * Tint per colour key, taken in sorted key order from the palette above, whose
 * order is chosen so the first colours are as far apart as possible and later
 * ones fill the gaps: a few groups/people on screen get clearly different
 * colours, and more of them get progressively closer ones (reused past 50).
 * A key's colour depends on who else is in the current view.
 */
const tintByKey = computed(() => {
  const keys = new Set<string>()
  for (const b of bookings.value) {
    const key = tintKey(b)
    if (key !== null) keys.add(key)
  }
  const map = new Map<string, (typeof TINT_COLORS)[number]>()
  Array.from(keys)
    .sort()
    .forEach((key, i) => map.set(key, TINT_COLORS[i % TINT_COLORS.length]!))
  return map
})

/**
 * Tint for a booking, or null for the plain styles (own = green, everything
 * the viewer isn't allowed to see = grey). Admin/owner get every booking
 * coloured — group sessions by group, anyone else's booking by person;
 * other viewers only get a colour for sessions of groups they perform in.
 */
function bookingTint(b: ScheduleBooking | null): (typeof TINT_COLORS)[number] | null {
  if (!b) return null
  const key = tintKey(b)
  return key === null ? null : (tintByKey.value.get(key) ?? null)
}

function tintStyle(b: ScheduleBooking | null): Record<string, string | number> | undefined {
  const tint = bookingTint(b)
  return tint === null
    ? undefined
    : { '--bsv-hue': tint.hue, '--bsv-sat': `${tint.sat}%`, '--bsv-lit': `${tint.lit}%` }
}

interface CellVM {
  booking: ScheduleBooking | null
  own: boolean
  managed: boolean
  isStart: boolean
}

// ── Drag-to-select a range, then ask for a reason ───────────────────────────

const dragAnchorDay = ref<number | null>(null)
const dragAnchorSlot = ref<number | null>(null)
const dragCursorSlot = ref<number | null>(null)
const isDragging = ref(false)

function startDrag(day: number, slot: number): void {
  if (
    selectedTypeId.value === null ||
    myPersonId.value === null ||
    !canCreateBookings.value ||
    bookingAt(day, slot)
  )
    return
  dragAnchorDay.value = day
  dragAnchorSlot.value = slot
  dragCursorSlot.value = slot
  isDragging.value = true
}
function extendDrag(day: number, slot: number): void {
  if (!isDragging.value || dragAnchorDay.value !== day) return
  const anchor = dragAnchorSlot.value ?? slot
  // Clamp to the contiguous free run from the anchor towards `slot` — a
  // booked cell must never enter the *live* selection highlight either,
  // not just the final range `endDrag` commits on mouseup.
  let cursor = anchor
  if (slot > anchor) {
    while (cursor < slot && !bookingAt(day, cursor + 1)) cursor++
  } else if (slot < anchor) {
    while (cursor > slot && !bookingAt(day, cursor - 1)) cursor--
  }
  dragCursorSlot.value = cursor
}
function endDrag(): void {
  if (!isDragging.value || dragAnchorDay.value === null || dragAnchorSlot.value === null) {
    isDragging.value = false
    return
  }
  const day = dragAnchorDay.value
  let from = Math.min(dragAnchorSlot.value, dragCursorSlot.value ?? dragAnchorSlot.value)
  let to = Math.max(dragAnchorSlot.value, dragCursorSlot.value ?? dragAnchorSlot.value)
  // Re-validate the whole range is free (fast mouse movement can skip enter
  // events); clamp to the contiguous free run that still includes the anchor.
  const anchor = dragAnchorSlot.value
  let clampedFrom = anchor
  while (clampedFrom > from && !bookingAt(day, clampedFrom - 1)) clampedFrom--
  let clampedTo = anchor
  while (clampedTo < to && !bookingAt(day, clampedTo + 1)) clampedTo++
  from = clampedFrom
  to = clampedTo
  isDragging.value = false
  pendingSelection.value = { day, from, to }
  bookingReason.value = ''
  resetBookingPersonToSelf()
}

function onDocumentMouseUp(): void {
  if (isDragging.value) endDrag()
}
onMounted(() => document.addEventListener('mouseup', onDocumentMouseUp))
onUnmounted(() => document.removeEventListener('mouseup', onDocumentMouseUp))

function isSelected(day: number, slot: number): boolean {
  if (dragAnchorDay.value === day && isDragging.value) {
    const from = Math.min(dragAnchorSlot.value ?? slot, dragCursorSlot.value ?? slot)
    const to = Math.max(dragAnchorSlot.value ?? slot, dragCursorSlot.value ?? slot)
    return slot >= from && slot <= to
  }
  if (pendingSelection.value && pendingSelection.value.day === day) {
    return slot >= pendingSelection.value.from && slot <= pendingSelection.value.to
  }
  return false
}

/**
 * `grid[slot][day]` — one view-model per cell, precomputed so the template
 * does plain property access instead of repeated `bookingAt(day, slot)`
 * calls inline in the markup. Deliberately excludes drag-selection
 * highlighting (`isSelected`, called directly from the template instead):
 * that's volatile state that changes on every `mouseenter` during a drag
 * gesture, and baking it in here would force all 7×`SLOTS` cells to be
 * rebuilt (re-deriving `bookingAt`/`isOwn`/`canManage`/`isSlotStart` for
 * every one of them) on every single pointer move, not just the dragged
 * column.
 */
const grid = computed<CellVM[][]>(() =>
  Array.from({ length: SLOTS }, (_, slot) =>
    Array.from({ length: 7 }, (_, day) => {
      const booking = bookingAt(day, slot)
      return {
        booking,
        own: booking !== null && isOwn(booking),
        managed: booking !== null && canManage(booking),
        isStart: isSlotStart(day, slot),
      }
    }),
  ),
)

// ── Reason dialog (create) ──────────────────────────────────────────────────

interface PendingSelection {
  day: number
  from: number
  to: number
}
const pendingSelection = ref<PendingSelection | null>(null)
// Declared here, not beside the edit dialog state below: `personPickerActive`
// is read synchronously during setup (by `useQuery`'s `enabled`), so this ref
// must be initialised before it.
const editingBooking = ref<ScheduleBooking | null>(null)
const bookingReason = ref('')

// ── Booking-for-others person picker (admin/owner only) ─────────────────────
// Defaults to the logged-in person; picking someone else only changes who
// the *new* booking is created for, never who is viewing the calendar.

interface PersonOption {
  id: string
  name: string
}

const personQuery = ref('')
/**
 * Tracks whether `personQuery` reflects an actual keystroke rather than
 * just the pre-filled "book for myself" default. Without this, opening the
 * dropdown while the box still shows your own name would filter the list
 * down to matches for *that* name — i.e. yourself and no one else, which
 * looks exactly like "there's no one else to pick". `filteredPersonOptions`
 * only applies the substring filter once this is `true`.
 */
const personQueryTouched = ref(false)
const personDropdownOpen = ref(false)
const selectedBookingPersonId = ref<string | null>(null) // null = book for self
// Two separate refs, one per dialog: both `.bsv__dlg-back` wrappers stay
// mounted at all times (hidden via the `hidden` attribute, not `v-if`), so
// a single shared ref would just end up pointing at whichever dialog's
// element bound it last, regardless of which dialog is actually open.
const personPickerRef = ref<HTMLElement | null>(null)
const editPersonPickerRef = ref<HTMLElement | null>(null)

function resetBookingPersonToSelf(): void {
  selectedBookingPersonId.value = null
  personQuery.value = myDisplayName.value
  personQueryTouched.value = false
  personDropdownOpen.value = false
}

function handleOutsidePersonPickerMousedown(e: MouseEvent): void {
  // The create and edit dialogs are mutually exclusive in practice (each
  // renders a full-viewport backdrop that blocks clicks reaching the other
  // trigger while it's open), so `pendingSelection` alone is enough to pick
  // the one relevant ref.
  const activeRef =
    pendingSelection.value !== null ? personPickerRef.value : editPersonPickerRef.value
  if (activeRef && !activeRef.contains(e.target as Node)) {
    personDropdownOpen.value = false
  }
}
onMounted(() => document.addEventListener('mousedown', handleOutsidePersonPickerMousedown))
onUnmounted(() => document.removeEventListener('mousedown', handleOutsidePersonPickerMousedown))

/**
 * Gates the performer/client fetches below on the picker actually being
 * visible, not merely on `canBookForOthers` — every admin/owner opening
 * this tab was otherwise fetching the full performer list and a clients
 * search on mount, whether or not they ever drag-select to create a
 * booking or open an existing one to edit. `pendingSelection`/
 * `editingBooking` are declared above; `useQuery` reads `enabled`
 * synchronously during setup, so they must be initialised before this.
 */
const personPickerActive = computed(
  () =>
    canBookForOthers.value && (pendingSelection.value !== null || editingBooking.value !== null),
)

// Performers: a small, mostly-static list — drained fully once, the same
// pattern `AddServiceForm` uses for its own performer picker.
const performersForPickerQuery = useInfiniteQuery({
  queryKey: ['performers'],
  queryFn: ({ pageParam }) => fetchPerformers(pageParam as number | undefined),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  enabled: personPickerActive,
})
watch(
  () => ({
    hasNext: performersForPickerQuery.hasNextPage.value === true,
    fetching: performersForPickerQuery.isFetchingNextPage.value === true,
  }),
  ({ hasNext, fetching }) => {
    if (hasNext && !fetching) void performersForPickerQuery.fetchNextPage()
  },
  { immediate: true, flush: 'post' },
)
const performerPickerOptions = computed<PersonOption[]>(() =>
  (performersForPickerQuery.data.value?.pages ?? []).flatMap((page) =>
    (page.items ?? []).map((p) => ({ id: p.id, name: p.name })),
  ),
)

// Clients: the list can be large, so it is never drained wholesale like
// performers above. Before the admin types anything, this fetches one
// unfiltered page (whatever order the backend defaults to) so clients are
// at least browsable alongside performers, not invisible until you search;
// once touched, the same call re-runs with the typed text as a server-side
// name filter instead.
const clientPickerSearchQuery = useQuery({
  queryKey: ['clientsSearch', personQueryTouched, personQuery],
  queryFn: () => fetchClients(undefined, personQueryTouched.value ? personQuery.value : undefined),
  enabled: personPickerActive,
})
const clientPickerOptions = computed<PersonOption[]>(() =>
  (clientPickerSearchQuery.data.value?.items ?? []).map((c) => ({ id: c.id, name: c.name })),
)

// Capped per source, not on the combined list: staff is typically a much
// shorter list than clients (tens vs. hundreds+), so a single cap applied
// after concatenating performers-then-clients let performers fill the
// whole cap and left clients needing a scroll to the very bottom to find
// even one — indistinguishable from "there are no clients" at a glance.
const PERFORMER_OPTIONS_CAP = 10
const CLIENT_OPTIONS_CAP = 15

const filteredPersonOptions = computed<PersonOption[]>(() => {
  const q = personQueryTouched.value ? personQuery.value.trim().toLowerCase() : ''
  const matchingPerformers = q
    ? performerPickerOptions.value.filter((p) => p.name.toLowerCase().includes(q))
    : performerPickerOptions.value
  // A Map de-dupes by id in case the same person is somehow returned by both
  // sources; clients are added last so a client-role match wins the name.
  const merged = new Map<string, PersonOption>()
  for (const p of matchingPerformers.slice(0, PERFORMER_OPTIONS_CAP)) merged.set(p.id, p)
  for (const c of clientPickerOptions.value.slice(0, CLIENT_OPTIONS_CAP)) merged.set(c.id, c)
  return Array.from(merged.values())
})

function selectBookingPerson(p: PersonOption): void {
  selectedBookingPersonId.value = p.id
  personQuery.value = p.name
  personQueryTouched.value = false
  personDropdownOpen.value = false
}

const pendingRangeLabel = computed(() => {
  const sel = pendingSelection.value
  if (!sel) return ''
  // sel.day is always 0-6, set from grid coordinates (see PendingSelection).
  const day = weekDays.value[sel.day]!
  return `${dayFmt.value.format(day)} ${appParts(day).day}, ${slotLabel(sel.from)}–${slotLabel(sel.to + 1)}`
})

const queryClient = useQueryClient()

const createMutation = useMutation({
  mutationFn: createBooking,
  onSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['bookingSchedule'] })
    pendingSelection.value = null
    bookingReason.value = ''
  },
})

function confirmCreate(): void {
  const sel = pendingSelection.value
  if (!sel || selectedTypeId.value === null || !canCreateBookings.value) return
  const personId =
    canBookForOthers.value && selectedBookingPersonId.value !== null
      ? selectedBookingPersonId.value
      : myPersonId.value
  if (personId === null) return
  const reason = bookingReason.value.trim() || null
  createMutation.mutate({
    booking_type_id: selectedTypeId.value,
    person_id: personId,
    start_time: dateAtSlot(monday.value, sel.day, sel.from).toISOString(),
    end_time: dateAtSlot(monday.value, sel.day, sel.to + 1).toISOString(),
    title: reason,
    description: reason,
  })
}
function cancelCreate(): void {
  pendingSelection.value = null
  bookingReason.value = ''
  createMutation.reset()
}

const timeOfDayFmt = computed(
  () =>
    new Intl.DateTimeFormat(intlLocale.value, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: APP_TIME_ZONE,
    }),
)

/** "HH:MM" formatted from a booking's start/end, shown inside its label. */
function bookingTimeRange(b: ScheduleBooking): string {
  return `${timeOfDayFmt.value.format(new Date(b.start_time))}–${timeOfDayFmt.value.format(new Date(b.end_time))}`
}

// ── Edit an own booking's time/reason ───────────────────────────────────────
// Clicking an own booking opens this dialog rather than cancelling directly
// — cancelling is a secondary action reached from inside it (`requestCancelFromEdit`),
// so a stray click no longer immediately threatens the booking.

// `editingBooking` itself is declared next to `pendingSelection` above.
const editStart = ref('') // "HH:MM", 24h, from an <input type="time">
const editEnd = ref('')
const editReason = ref('')

/** "HH:MM" for an <input type="time">, in the app's UTC+3 zone. */
function timeInputValue(d: Date): string {
  return appTimeOfDay(d)
}

function onCellClick(b: ScheduleBooking): void {
  editingBooking.value = b
  editReason.value = b.title ?? ''
  editStart.value = timeInputValue(new Date(b.start_time))
  editEnd.value = timeInputValue(new Date(b.end_time))
  if (isOwn(b)) {
    // Same reasoning as `endDrag`: "self" is the accurate starting point.
    resetBookingPersonToSelf()
  } else {
    // Admin/owner managing someone else's booking (see `canManage`) —
    // default the picker to whoever it's actually for, not to self, so
    // saving without touching this field doesn't silently reassign it.
    selectedBookingPersonId.value = b.person_id
    personQuery.value = personDisplayName(b.person_id)
    personQueryTouched.value = false
    personDropdownOpen.value = false
  }
}

const editDayLabel = computed(() => {
  const b = editingBooking.value
  if (!b) return ''
  const day = new Date(b.start_time)
  return `${dayFmt.value.format(day)} ${appParts(day).day}`
})

/**
 * The grid only has half-hour slots — `isSlotStart` requires exact
 * millisecond equality against a `dateAtSlot` boundary — so a saved time
 * off that grid (e.g. typed as "08:07"; the `step` attribute on the
 * `<input type="time">` only constrains its native picker/spinner, not
 * freely typed text) would still show the cell as occupied but silently
 * drop its who/time/reason label, since that label is gated on
 * `cell.isStart`.
 */
function isHalfHourAligned(hhmm: string): boolean {
  const minute = Number(hhmm.split(':')[1] ?? NaN)
  return minute === 0 || minute === 30
}

// Plain string comparison works for zero-padded "HH:MM": lexical order
// matches chronological order within a single day.
//
// A field is valid if it's half-hour aligned OR left exactly as it was
// prefilled from the booking (see `onCellClick`): a legacy booking (or one
// created by any non-UI path) can start/end off the grid, and without this
// escape hatch, simply opening it for edit — to change only the reason or
// the person — would be permanently blocked since its own untouched times
// could never pass `isHalfHourAligned`.
const editTimeValid = computed(() => {
  const b = editingBooking.value
  if (editStart.value === '' || editEnd.value === '' || editStart.value >= editEnd.value) {
    return false
  }
  const startOk =
    isHalfHourAligned(editStart.value) ||
    (b !== null && editStart.value === timeInputValue(new Date(b.start_time)))
  const endOk =
    isHalfHourAligned(editEnd.value) ||
    (b !== null && editEnd.value === timeInputValue(new Date(b.end_time)))
  return startOk && endOk
})

const updateMutation = useMutation({
  mutationFn: ({ id, payload }: { id: string; payload: UpdateBookingPayload }) =>
    updateBooking(id, payload),
  onSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['bookingSchedule'] })
    editingBooking.value = null
  },
})

function confirmEdit(): void {
  const b = editingBooking.value
  if (!b || !editTimeValid.value || !canEditBookings.value) return
  const personId =
    canBookForOthers.value && selectedBookingPersonId.value !== null
      ? selectedBookingPersonId.value
      : myPersonId.value
  if (personId === null) return
  // Only the time of day changes here — the booking stays on the day it was
  // created on; moving it to a different day isn't exposed by this dialog.
  const day = appParts(new Date(b.start_time))
  const [startH, startM] = editStart.value.split(':').map(Number)
  const [endH, endM] = editEnd.value.split(':').map(Number)
  const start = appDate(day.year, day.month, day.day, startH ?? 0, startM ?? 0)
  const end = appDate(day.year, day.month, day.day, endH ?? 0, endM ?? 0)
  const reason = editReason.value.trim() || null
  updateMutation.mutate({
    id: b.id,
    payload: {
      person_id: personId,
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      title: reason,
      description: reason,
    },
  })
}
function cancelEdit(): void {
  editingBooking.value = null
  updateMutation.reset()
}

// ── Cancel an own booking (reached from inside the edit dialog) ────────────

const bookingToCancel = ref<ScheduleBooking | null>(null)
const cancelMutation = useMutation({
  mutationFn: (id: string) => cancelBooking(id),
  onSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['bookingSchedule'] })
    bookingToCancel.value = null
  },
})

function requestCancelFromEdit(): void {
  if (!editingBooking.value || !canCancelBookings.value) return
  bookingToCancel.value = editingBooking.value
  editingBooking.value = null
}
const cancelDescription = computed(() => {
  const b = bookingToCancel.value
  if (!b) return ''
  const start = new Date(b.start_time)
  const end = new Date(b.end_time)
  const time = `${start.toLocaleDateString(intlLocale.value, { timeZone: APP_TIME_ZONE })} ${timeOfDayFmt.value.format(start)}–${timeOfDayFmt.value.format(end)}`
  return b.title
    ? t('tabs.booking.cancelDescriptionWithReason', { time, reason: b.title })
    : t('tabs.booking.cancelDescription', { time })
})
function confirmCancel(): void {
  if (bookingToCancel.value) cancelMutation.mutate(bookingToCancel.value.id)
}
function dismissCancel(): void {
  bookingToCancel.value = null
}
</script>

<template>
  <div class="bsv" :class="{ 'bsv--mobile': isMobile }">
    <div class="bsv__rooms" role="tablist">
      <button
        v-for="bt in bookingTypes"
        :key="bt.id"
        type="button"
        role="tab"
        class="bsv__room"
        :class="{ 'bsv__room--active': bt.id === selectedTypeId }"
        :aria-selected="bt.id === selectedTypeId"
        @click="selectedTypeId = bt.id"
      >
        {{ bt.name ?? t('cards.bookingType.defaultTypeName') }}
      </button>
      <span v-if="bookingTypes.length === 0 && !typesIsDraining" class="bsv__no-types">
        {{ t('tabs.booking.noTypes') }}
      </span>
    </div>

    <div class="bsv__week-bar">
      <span v-if="myPersonId === null" class="bsv__no-person-note">
        {{ t('tabs.booking.noPersonId') }}
      </span>
      <div class="bsv__week-nav">
        <button type="button" @click="shiftWeek(-1)">‹</button>
        <span class="bsv__week-label">{{ weekLabel }}</span>
        <button type="button" @click="shiftWeek(1)">›</button>
        <button type="button" @click="goToday">{{ t('tabs.booking.today') }}</button>
      </div>
    </div>

    <!--
      The calendar frame itself always renders — an empty week is a valid,
      useful state, not an error. Only the two branches above it are
      conditional: `isScheduleLoading` skips the (permanent, since a
      disabled query never settles) `isPending` state when no booking type
      is selected — there's nothing to load, so the empty grid below is
      already the correct "loaded" view, not a stuck spinner.
    -->
    <p v-if="isScheduleLoading" class="text-tufte-muted">
      {{ t('tabs.booking.loading') }}
    </p>
    <p v-else-if="selectedTypeId !== null && scheduleQuery.isError.value" class="text-red-600">
      {{ t('tabs.booking.loadError', { message: t('common.unknownError') }) }}
    </p>
    <div
      v-else
      class="bsv__cal"
      :class="{ 'bsv__cal--disabled': selectedTypeId === null }"
      @mouseleave="isDragging && endDrag()"
    >
      <div class="bsv__cal-head">
        <div />
        <div
          v-for="(day, i) in weekDays"
          :key="i"
          :class="{ bsv__today: appDateString(day) === appDateString(today) }"
        >
          {{ dayFmt.format(day) }}
          <span class="bsv__dnum">{{ appParts(day).day }}</span>
        </div>
      </div>
      <div class="bsv__cal-body" @mousedown.prevent>
        <!-- `slotIdx`/`dayIdx` below are 0-indexed (Vue's `v-for="(x, i) in array"`
        form), matching every function above; only even `slotIdx` values land
        on the hour and show a time label, the odd half-hour row in between
        is dotted and unlabeled (`.bsv__half`), matching the sketch. -->
        <div
          v-for="(row, slotIdx) in grid"
          :key="slotIdx"
          class="bsv__cal-row"
          :class="{ bsv__half: slotIdx % 2 === 1 }"
        >
          <div class="bsv__t">{{ slotIdx % 2 === 0 ? slotLabel(slotIdx) : '' }}</div>
          <div
            v-for="(cell, dayIdx) in row"
            :key="dayIdx"
            class="bsv__cell"
            :class="{
              'bsv__cell--busy': cell.booking !== null,
              'bsv__cell--own': cell.own,
              'bsv__cell--blind': cell.booking !== null && !cell.own,
              'bsv__cell--tint': bookingTint(cell.booking) !== null,
              'bsv__cell--group':
                cell.booking !== null &&
                cell.booking.group_id !== null &&
                bookingTint(cell.booking) !== null,
              'bsv__cell--managed': cell.managed && !cell.own,
              'bsv__cell--start': cell.isStart,
              'bsv__cell--abut':
                cell.isStart && slotIdx > 0 && bookingAt(dayIdx, slotIdx - 1) !== null,
              'bsv__cell--selected': isSelected(dayIdx, slotIdx),
            }"
            :style="tintStyle(cell.booking)"
            @mousedown="startDrag(dayIdx, slotIdx)"
            @mouseenter="extendDrag(dayIdx, slotIdx)"
            @click="cell.managed && cell.booking && onCellClick(cell.booking)"
          >
            <span
              v-if="cell.booking && cell.isStart && (cell.managed || groupInfo(cell.booking))"
              class="bsv__lbl"
            >
              <template v-if="groupInfo(cell.booking)">
                <span class="bsv__group-tag">{{ t('tabs.booking.groupTag') }}</span>
                <span class="bsv__who">{{ groupInfo(cell.booking)!.name }}</span>
                <span class="bsv__time">{{ bookingTimeRange(cell.booking) }}</span>
                <span v-if="groupInfo(cell.booking)!.performers" class="bsv__reason">{{
                  groupInfo(cell.booking)!.performers
                }}</span>
              </template>
              <template v-else>
                <span class="bsv__who">{{
                  cell.own ? myDisplayName : personDisplayName(cell.booking.person_id)
                }}</span>
                <span class="bsv__time">{{ bookingTimeRange(cell.booking) }}</span>
                <span v-if="cell.booking.title" class="bsv__reason">{{ cell.booking.title }}</span>
              </template>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="bsv__legend">
      <span class="bsv__k"
        ><span class="bsv__sw bsv__sw--own" />{{ t('tabs.booking.legendOwn') }}</span
      >
      <span class="bsv__k"
        ><span class="bsv__sw bsv__sw--blind" />{{ t('tabs.booking.legendBusy') }}</span
      >
      <span class="bsv__k"
        ><span class="bsv__sw bsv__sw--group" />{{ t('tabs.booking.legendGroup') }}</span
      >
      <span class="bsv__k"
        ><span class="bsv__sw bsv__sw--free" />{{ t('tabs.booking.legendFree') }}</span
      >
    </div>

    <!-- Create-booking reason dialog -->
    <div class="bsv__dlg-back" :hidden="!pendingSelection" @click="cancelCreate">
      <div class="bsv__dlg" role="dialog" aria-modal="true" @click.stop>
        <div class="bsv__dlg-h">{{ t('tabs.booking.newBookingTitle') }}</div>
        <div class="bsv__dlg-b">
          <p class="bsv__dlg-range">{{ pendingRangeLabel }}</p>

          <div v-if="canBookForOthers" ref="personPickerRef" class="bsv__dlg-person">
            <label class="bsv__dlg-label" for="bsv-person">{{
              t('tabs.booking.personLabel')
            }}</label>
            <div class="bsv__dlg-person-input-wrap">
              <input
                id="bsv-person"
                v-model="personQuery"
                type="text"
                class="bsv__dlg-time-input"
                autocomplete="off"
                @input="personQueryTouched = true"
                @focus="personDropdownOpen = true"
                @click="personDropdownOpen = true"
              />
              <button
                v-if="selectedBookingPersonId !== null"
                type="button"
                class="bsv__dlg-person-reset"
                @click="resetBookingPersonToSelf"
              >
                {{ t('tabs.booking.personResetToSelf') }}
              </button>
              <div
                v-if="personDropdownOpen && filteredPersonOptions.length > 0"
                class="bsv__dlg-person-dropdown"
              >
                <div
                  v-for="p in filteredPersonOptions"
                  :key="p.id"
                  class="bsv__dlg-person-option"
                  @mousedown.prevent="selectBookingPerson(p)"
                >
                  {{ p.name }}
                </div>
              </div>
            </div>
          </div>

          <label class="bsv__dlg-label" for="bsv-reason">{{ t('tabs.booking.reasonLabel') }}</label>
          <textarea
            id="bsv-reason"
            v-model="bookingReason"
            class="bsv__dlg-textarea"
            :placeholder="t('tabs.booking.reasonPlaceholder')"
            autofocus
          />
          <p v-if="createMutation.isError.value" class="bsv__dlg-error">
            {{ t('tabs.booking.createFailed') }}
          </p>
        </div>
        <div class="bsv__dlg-f">
          <button type="button" @click="cancelCreate">{{ t('common.cancel') }}</button>
          <button
            type="button"
            class="bsv__dlg-primary"
            :disabled="createMutation.isPending.value || !canCreateBookings"
            @click="confirmCreate"
          >
            {{
              createMutation.isPending.value ? t('common.creating') : t('tabs.booking.confirmBook')
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- Edit-booking dialog (time + reason) -->
    <div class="bsv__dlg-back" :hidden="!editingBooking" @click="cancelEdit">
      <div class="bsv__dlg" role="dialog" aria-modal="true" @click.stop>
        <div class="bsv__dlg-h">{{ t('tabs.booking.editBookingTitle') }}</div>
        <div class="bsv__dlg-b">
          <p class="bsv__dlg-range">{{ editDayLabel }}</p>

          <div v-if="canBookForOthers" ref="editPersonPickerRef" class="bsv__dlg-person">
            <label class="bsv__dlg-label" for="bsv-edit-person">{{
              t('tabs.booking.personLabel')
            }}</label>
            <div class="bsv__dlg-person-input-wrap">
              <input
                id="bsv-edit-person"
                v-model="personQuery"
                type="text"
                class="bsv__dlg-time-input"
                autocomplete="off"
                @input="personQueryTouched = true"
                @focus="personDropdownOpen = true"
                @click="personDropdownOpen = true"
              />
              <button
                v-if="selectedBookingPersonId !== null"
                type="button"
                class="bsv__dlg-person-reset"
                @click="resetBookingPersonToSelf"
              >
                {{ t('tabs.booking.personResetToSelf') }}
              </button>
              <div
                v-if="personDropdownOpen && filteredPersonOptions.length > 0"
                class="bsv__dlg-person-dropdown"
              >
                <div
                  v-for="p in filteredPersonOptions"
                  :key="p.id"
                  class="bsv__dlg-person-option"
                  @mousedown.prevent="selectBookingPerson(p)"
                >
                  {{ p.name }}
                </div>
              </div>
            </div>
          </div>

          <div class="bsv__dlg-time-row">
            <div class="bsv__dlg-time-field">
              <label class="bsv__dlg-label" for="bsv-edit-start">{{
                t('tabs.booking.startLabel')
              }}</label>
              <input
                id="bsv-edit-start"
                v-model="editStart"
                type="time"
                step="1800"
                class="bsv__dlg-time-input"
                autofocus
              />
            </div>
            <div class="bsv__dlg-time-field">
              <label class="bsv__dlg-label" for="bsv-edit-end">{{
                t('tabs.booking.endLabel')
              }}</label>
              <input
                id="bsv-edit-end"
                v-model="editEnd"
                type="time"
                step="1800"
                class="bsv__dlg-time-input"
              />
            </div>
          </div>
          <p v-if="!editTimeValid" class="bsv__dlg-error">
            {{ t('tabs.booking.timeRangeInvalid') }}
          </p>
          <label class="bsv__dlg-label" for="bsv-edit-reason">{{
            t('tabs.booking.reasonLabel')
          }}</label>
          <textarea
            id="bsv-edit-reason"
            v-model="editReason"
            class="bsv__dlg-textarea"
            :placeholder="t('tabs.booking.reasonPlaceholder')"
          />
          <p v-if="updateMutation.isError.value" class="bsv__dlg-error">
            {{ t('tabs.booking.updateFailed') }}
          </p>
        </div>
        <div class="bsv__dlg-f">
          <button
            v-if="canCancelBookings"
            type="button"
            class="bsv__dlg-danger"
            @click="requestCancelFromEdit"
          >
            {{ t('tabs.booking.confirmCancelBooking') }}
          </button>
          <button type="button" @click="cancelEdit">{{ t('common.cancel') }}</button>
          <button
            type="button"
            class="bsv__dlg-primary"
            :disabled="!editTimeValid || updateMutation.isPending.value || !canEditBookings"
            @click="confirmEdit"
          >
            {{ updateMutation.isPending.value ? t('common.saving') : t('common.save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Cancel-booking confirm -->
    <DeleteConfirmSheet
      :open="bookingToCancel !== null"
      centered
      :title="t('tabs.booking.cancelTitle')"
      :description="cancelDescription"
      :confirm-label="t('tabs.booking.confirmCancelBooking')"
      :pending="cancelMutation.isPending.value"
      @confirm="confirmCancel"
      @cancel="dismissCancel"
    />
  </div>
</template>

<style scoped>
.bsv {
  font-family: var(--font-tufte);
  color: var(--color-tufte-text);
}

/*
 * Mobile layout — matches docs/sketches/mobile/booking-schedule.html:
 * rooms become a horizontally-scrolling row of pills (desktop's underline
 * tabs don't fit many room names on a phone width), the calendar's time
 * gutter narrows and its rows grow to 32px (desktop's 1.45rem/~23px rows
 * are fine for a mouse, too small to reliably tap), and
 * `<article>`'s side padding — stripped to 0 on mobile by `MobileMain.vue`
 * — is added back on the few rows that need it (the calendar grid itself
 * stays edge-to-edge, same convention as every other mobile table in this
 * app). No JS branching for the booking interaction itself: a tap
 * synthesizes the same mousedown → mouseup sequence `startDrag`/`endDrag`
 * already handle, so it naturally resolves to a single-slot selection
 * (matching the sketch's own tap-to-occupy mobile behavior) without a
 * separate code path — multi-slot drag-select just isn't reachable via
 * touch the way it is with a mouse.
 */
.bsv--mobile .bsv__rooms {
  padding: 0 1.25rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 0.5rem;
  border-bottom: none;
  padding-bottom: 0.6rem;
}
.bsv--mobile .bsv__room {
  flex-shrink: 0;
  border: 1px solid var(--color-tufte-border);
  border-radius: 999px;
  padding: 0.45rem 0.9rem;
  min-height: 38px;
  white-space: nowrap;
}
.bsv--mobile .bsv__room--active {
  background: color-mix(in srgb, var(--color-tufte-success) 16%, var(--color-tufte-bg));
  border-color: color-mix(in srgb, var(--color-tufte-success) 45%, var(--color-tufte-border));
}
.bsv--mobile .bsv__no-types,
.bsv--mobile .bsv__week-bar,
.bsv--mobile .bsv__legend {
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}
.bsv--mobile .bsv__cal {
  border-left: none;
  border-right: none;
  border-radius: 0;
}
.bsv--mobile .bsv__cal-head,
.bsv--mobile .bsv__cal-row {
  grid-template-columns: 2.6rem repeat(7, minmax(0, 1fr));
}
.bsv--mobile .bsv__cal-row {
  height: 32px;
}
.bsv--mobile .bsv__t {
  font-size: 0.64rem;
  padding-right: 0.25rem;
  line-height: 32px;
}
.bsv--mobile .bsv__week-bar {
  gap: 0.5rem;
}
.bsv--mobile .bsv__week-label {
  min-width: 0;
  font-size: var(--text-tufte-sm);
}
.bsv--mobile .bsv__week-nav {
  gap: 0.35rem;
}
.bsv--mobile .bsv__week-nav button {
  padding: 0.35rem 0.5rem;
}

.bsv__rooms {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--color-tufte-border);
  flex-wrap: wrap;
}
.bsv__room {
  font-family: var(--font-tufte);
  font-size: var(--text-tufte-lg);
  color: var(--color-tufte-muted);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 0.5rem 1.05rem 0.6rem;
  margin-bottom: -1px;
  cursor: pointer;
}
.bsv__room:hover {
  color: var(--color-tufte-text);
}
.bsv__room--active {
  color: var(--color-tufte-text);
  border-bottom-color: var(--color-tufte-text);
}
.bsv__no-types {
  padding: 0.5rem 0;
  color: var(--color-tufte-muted);
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-sm);
}

.bsv__week-bar {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
  padding: 1rem 0 0.8rem;
  font-family: var(--font-tufte-sans);
}
.bsv__no-person-note {
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-danger);
}
.bsv__week-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}
.bsv__week-nav button {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-sm);
  color: var(--color-tufte-text);
  background: var(--color-tufte-bg);
  border: 1px solid var(--color-tufte-border);
  padding: 0.35rem 0.65rem;
  cursor: pointer;
}
.bsv__week-nav button:hover {
  background: color-mix(in srgb, var(--color-tufte-text) 6%, transparent);
}
.bsv__week-label {
  font-family: var(--font-tufte);
  font-size: var(--text-tufte-sm);
  min-width: 11rem;
  text-align: center;
}

.bsv__cal {
  border: 1px solid var(--color-tufte-border);
  border-radius: 4px;
  overflow: hidden;
}
/*
 * No booking type to book into (`selectedTypeId === null`, i.e. the "Пока
 * нет типов записи" case) — `startDrag` already refuses to start a
 * selection in this state; `pointer-events: none` backs that up so a touch
 * device (where that JS guard is the only thing stopping a tap, not a
 * `:hover`/cursor affordance) can't reach the cells either, and the muted
 * opacity plus `not-allowed` cursor tell a mouse user why before they try.
 */
.bsv__cal--disabled {
  opacity: 0.55;
}
.bsv__cal--disabled .bsv__cal-body {
  pointer-events: none;
}
.bsv__cal--disabled .bsv__cell {
  cursor: not-allowed;
}
.bsv__cal-head,
.bsv__cal-row {
  display: grid;
  grid-template-columns: 3.6rem repeat(7, minmax(0, 1fr));
}
.bsv__cal-head {
  border-bottom: 1px solid var(--color-tufte-border);
  background: color-mix(in srgb, var(--color-tufte-border) 15%, var(--color-tufte-bg));
}
.bsv__cal-head div {
  padding: 0.4rem 0.3rem 0.45rem;
  text-align: center;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  border-left: 1px solid var(--color-tufte-border-light);
}
.bsv__cal-head div:first-child {
  border-left: none;
}
.bsv__dnum {
  display: block;
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-sm);
  color: var(--color-tufte-text);
}
.bsv__today .bsv__dnum {
  color: var(--color-tufte-success);
  font-weight: 700;
}
.bsv__cal-body {
  /*
   * No max-height/overflow-y here: the routed middle column
   * (`.middle-column` in DesktopMain.vue) already scrolls as a whole, so
   * capping this at a fixed height just added a second, redundant scrollbar
   * and clipped the grid well above the bottom of the page. Rendering at
   * natural height (30 half-hour rows × 1.45rem ≈ 43.5rem) lets the whole
   * week extend down as far as it needs to.
   */
  user-select: none;
}
.bsv__cal-row {
  height: 1.45rem;
}
.bsv__t {
  font-family: var(--font-tufte-mono);
  font-size: 0.68rem;
  color: var(--color-tufte-muted);
  text-align: right;
  padding-right: 0.4rem;
  border-top: 1px solid var(--color-tufte-border-light);
  line-height: 1.45rem;
}
.bsv__cell {
  border-left: 1px solid var(--color-tufte-border-light);
  border-top: 1px solid var(--color-tufte-border-light);
  cursor: pointer;
  position: relative;
}
.bsv__half .bsv__cell {
  border-top: 1px dotted color-mix(in srgb, var(--color-tufte-border-light) 60%, transparent);
}
.bsv__cell:hover:not(.bsv__cell--busy) {
  background: color-mix(in srgb, var(--color-tufte-border) 20%, transparent);
}
.bsv__cell--busy {
  cursor: default;
}
/*
 * A booking that isn't the viewer's own reads as plain grey, matching the
 * `legendBusy` swatch below — no identifying color or label, just "busy".
 * This applies whether or not the viewer can manage it (`--managed` below
 * only adds `cursor: pointer`): admin/owner already see the name directly
 * on managed bookings, so a second, per-person color code on top of that
 * would be redundant, not clarifying.
 */
.bsv__cell--blind {
  background: color-mix(in srgb, var(--color-tufte-border) 45%, var(--color-tufte-bg));
}
/* Tinted bookings: `--bsv-hue/sat/lit` are set per booking (same group, same colour). */
.bsv__cell.bsv__cell--tint {
  background: hsl(var(--bsv-hue) var(--bsv-sat) var(--bsv-lit));
}
.bsv__cell--tint .bsv__who {
  color: hsl(var(--bsv-hue) var(--bsv-sat) 26%);
}
/* Group sessions: an accent bar down the booking's left edge. */
.bsv__cell--group {
  box-shadow: inset 3px 0 0 hsl(var(--bsv-hue) var(--bsv-sat) 38%);
}
.bsv__group-tag {
  display: block;
  width: fit-content;
  padding: 0 0.3rem;
  font-size: 0.62rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: hsl(var(--bsv-hue) var(--bsv-sat) 96%);
  color: hsl(var(--bsv-hue) var(--bsv-sat) 30%);
}
.bsv__cell--own {
  background: color-mix(in srgb, var(--color-tufte-success) 16%, var(--color-tufte-bg));
  cursor: pointer;
}
/*
 * No `:hover` rule here on purpose, matching `--managed` below: `:hover`
 * only ever applies to the single cell the cursor is physically over, so
 * a multi-slot booking would otherwise show one differently-shaded cell
 * in the middle of an otherwise uniform block whenever the mouse crossed
 * it — the same "visually separate cells" artifact this class used to
 * have, now fixed the same way for both.
 */
/*
 * A booking admin/owner can manage but doesn't own (`canManage` true,
 * `own` false) — keeps the grey `--blind` background above, just becomes
 * clickable like `--own` instead of `cursor: default`.
 *
 * No `:hover` rule here either, for the same reason as `--own` above.
 */
.bsv__cell--managed {
  cursor: pointer;
}
.bsv__cell--start {
  border-top-color: color-mix(in srgb, var(--color-tufte-text) 25%, transparent);
}
/*
 * A booking's continuation cells (busy, but not its first/`--start` cell)
 * drop the row divider entirely, so the whole booking reads as one
 * seamless block instead of a rectangle visibly cut by grid lines.
 * `--start` keeps its own top border above (the boundary against whatever
 * precedes the booking) — only the cells after it lose theirs.
 */
/* A booking starting right where another ends (even a half-hour row): a solid divider so back-to-back bookings don't merge into one block. */
.bsv__cell.bsv__cell--abut,
.bsv__half .bsv__cell.bsv__cell--abut {
  border-top: 1px solid color-mix(in srgb, var(--color-tufte-text) 45%, transparent);
}
.bsv__cell--busy:not(.bsv__cell--start) {
  border-top-color: transparent;
}
.bsv__cell--selected {
  background: color-mix(in srgb, var(--color-tufte-success) 30%, var(--color-tufte-bg));
}
.bsv__lbl {
  position: absolute;
  inset: 0.05rem 0.25rem auto;
  font-family: var(--font-tufte-sans);
  font-size: 0.8rem;
  line-height: 1.2;
  /*
   * A booking's label lives on its *first* cell (see `cell.isStart` in the
   * template) but its three lines (who/time/reason) can still run taller
   * than that one 1.45rem row. `z-index: 1` keeps it painted above the
   * opaque backgrounds of the (later-in-DOM) cells it spills into;
   * `pointer-events: none` then hands mouse/click events straight through
   * to whichever cell is actually underneath, so dragging a free cell the
   * label happens to overlap, or clicking a covered slot of the same
   * booking, still works exactly as if the label weren't there.
   */
  z-index: 1;
  pointer-events: none;
}
/*
 * Each line truncates to a single line with an ellipsis instead of
 * wrapping — `.bsv__lbl`'s `inset` above already fixes this box's width to
 * the cell's own width (minus a small margin), so an untruncated long name
 * or reason would otherwise wrap mid-word or spill sideways past the
 * cell's border into the next day's column.
 */
.bsv__who,
.bsv__time,
.bsv__reason {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.bsv__who {
  font-weight: 600;
  color: color-mix(in srgb, var(--color-tufte-success) 70%, var(--color-tufte-text));
}
.bsv__time {
  font-family: var(--font-tufte-mono);
  font-size: 0.72rem;
  color: var(--color-tufte-muted);
}
.bsv__reason {
  font-size: 0.85rem;
  font-style: italic;
  color: var(--color-tufte-secondary);
}

.bsv__legend {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  flex-wrap: wrap;
  margin-top: 0.8rem;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
}
.bsv__k {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}
.bsv__sw {
  width: 0.85rem;
  height: 0.85rem;
  border: 1px solid var(--color-tufte-border);
  display: inline-block;
}
.bsv__sw--own {
  background: color-mix(in srgb, var(--color-tufte-success) 16%, var(--color-tufte-bg));
}
.bsv__sw--blind {
  background: color-mix(in srgb, var(--color-tufte-border) 45%, var(--color-tufte-bg));
}
.bsv__sw--group {
  box-shadow: inset 3px 0 0 hsl(150 45% 38%);
  background: hsl(150 38% 88%);
}
.bsv__sw--free {
  background: var(--color-tufte-bg);
}

.bsv__dlg-back {
  position: fixed;
  inset: 0;
  background: rgba(20, 20, 15, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.bsv__dlg-back[hidden] {
  display: none;
}
.bsv__dlg {
  width: 24rem;
  max-width: calc(100% - 2rem);
  background: var(--color-tufte-bg);
  border: 1px solid var(--color-tufte-border);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}
.bsv__dlg-h {
  padding: 1rem 1.2rem 0.3rem;
  font-family: var(--font-tufte);
  font-size: var(--text-tufte-lg);
}
.bsv__dlg-b {
  padding: 0.5rem 1.2rem 1rem;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-sm);
}
.bsv__dlg-range {
  color: var(--color-tufte-muted);
  margin-bottom: 0.6rem;
}
.bsv__dlg-label {
  display: block;
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  margin-bottom: 0.25rem;
}
.bsv__dlg-textarea {
  width: 100%;
  min-height: 4rem;
  font-family: var(--font-tufte);
  font-size: var(--text-tufte-sm);
  color: var(--color-tufte-text);
  background: var(--color-tufte-bg);
  border: 1px solid var(--color-tufte-border);
  border-radius: 0;
  padding: 0.4rem 0.5rem;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
}
.bsv__dlg-textarea:focus {
  border-color: color-mix(in srgb, var(--color-tufte-text) 55%, transparent);
}
.bsv__dlg-time-row {
  display: flex;
  gap: 0.8rem;
  margin-bottom: 0.6rem;
}
.bsv__dlg-time-field {
  flex: 1;
}
.bsv__dlg-time-input {
  width: 100%;
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-sm);
  color: var(--color-tufte-text);
  background: var(--color-tufte-bg);
  border: 1px solid var(--color-tufte-border);
  border-radius: 0;
  padding: 0.35rem 0.5rem;
  outline: none;
  box-sizing: border-box;
}
.bsv__dlg-time-input:focus {
  border-color: color-mix(in srgb, var(--color-tufte-text) 55%, transparent);
}
.bsv__dlg-person {
  margin-bottom: 0.6rem;
}
.bsv__dlg-person-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.bsv__dlg-person-reset {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  flex-shrink: 0;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.02em;
  color: var(--color-tufte-muted);
  cursor: pointer;
  white-space: nowrap;
}
.bsv__dlg-person-reset:hover {
  color: var(--color-tufte-text);
}
.bsv__dlg-person-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 1px;
  background: var(--color-tufte-bg);
  border: 1px solid color-mix(in srgb, var(--color-tufte-text) 55%, transparent);
  z-index: 10;
  max-height: 10rem;
  overflow-y: auto;
}
.bsv__dlg-person-option {
  padding: 0.28rem 0.5rem;
  font-family: var(--font-tufte);
  font-size: var(--text-tufte-sm);
  cursor: pointer;
}
.bsv__dlg-person-option:hover {
  background: color-mix(in srgb, var(--color-tufte-border) 35%, transparent);
}
.bsv__dlg-error {
  color: var(--color-tufte-danger);
  font-size: var(--text-tufte-xs);
  margin-top: 0.5rem;
}
.bsv__dlg-f {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1.2rem;
  border-top: 1px solid var(--color-tufte-border);
}
.bsv__dlg-f button {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-sm);
  padding: 0.4rem 0.9rem;
  cursor: pointer;
  background: var(--color-tufte-bg);
  border: 1px solid var(--color-tufte-border);
  color: var(--color-tufte-text);
}
/*
 * Same outline style every other primary submit button in the app uses
 * (e.g. `.abtf__btn-submit` in AddBookingTypeForm) — a solid black fill
 * read as too heavy/out of place next to the plain-outline Cancel button.
 */
.bsv__dlg-primary {
  border-color: color-mix(in srgb, var(--color-tufte-text) 60%, transparent) !important;
}
.bsv__dlg-primary:hover:not(:disabled) {
  background: color-mix(in srgb, var(--color-tufte-text) 6%, transparent) !important;
}
/* Pushed to the far left of the footer (margin-right: auto) so the two
 * non-destructive actions (Cancel/Save) stay grouped on the right, matching
 * the sketch's own "Удалить" placement in the type-edit form. */
.bsv__dlg-danger {
  margin-right: auto;
  color: var(--color-tufte-danger) !important;
  border-color: color-mix(
    in srgb,
    var(--color-tufte-danger) 40%,
    var(--color-tufte-border)
  ) !important;
}
.bsv__dlg-danger:hover {
  background: color-mix(in srgb, var(--color-tufte-danger) 8%, var(--color-tufte-bg)) !important;
}
.bsv__dlg-primary:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
